import React, { useState, useEffect, useMemo } from 'react';
import { useFarm } from '../../contexts/FarmContext';
import { supabase } from '../../config/supabase';
import OrganizationSelector from '../../components/OrganizationSelector';
import FarmSelector from '../../components/FarmSelector';
import {
  FunnelIcon,
  CubeIcon,
  ReceiptPercentIcon,
  BanknotesIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import FarmSearchDropdown from '../../components/FarmSearchDropdown';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Cost = () => {
  const { farms, isAdmin, hasOrganizations, hasFarms, getActiveFarmIds, loading: farmLoading } = useFarm();
  const [costs, setCosts] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    totalCategories: 0,
    totalItems: 0,
    totalCost: 0,
    avgCostPerItem: 0
  });
  const [dateRange, setDateRange] = useState({
    start: '2025-01-01',
    end: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0]
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Load categories for filter
  useEffect(() => {
    const loadCategories = async () => {
      const { data } = await supabase
        .from('financial_transaction_subtype')
        .select('id, name')
        .order('name');
      if (data) setCategories(data.map(c => ({ id: c.id, category_name: c.name })));
    };
    loadCategories();
  }, []);

  // Get active farm IDs from context (filtered by organization)
  const activeFarmIds = getActiveFarmIds();
  const activeFarmIdsKey = JSON.stringify(activeFarmIds);

  // Fetch ALL data for stats (using pagination to bypass limits)
  const fetchAllData = async (farmIds) => {
    if (!farmIds || farmIds.length === 0) {
      console.log('💰 Cost: No active farms, returning empty');
      return [];
    }

    console.log('💰 Cost: Fetching data for', farmIds.length, 'farms');
    let allRecords = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      let query = supabase
        .from('financial_transaction_view')
        .select('*')
        .eq('in', false)
        .in('farm', farmIds)
        .order('date_transaction', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (dateRange.start) {
        query = query.gte('date_transaction', dateRange.start);
      }
      if (dateRange.end) {
        query = query.lte('date_transaction', dateRange.end);
      }
      if (selectedFarmFilter !== 'all') {
        query = query.eq('farm', parseInt(selectedFarmFilter));
      }
      if (selectedCategory !== 'all') {
        query = query.eq('fin_subtype_id', parseInt(selectedCategory));
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching data:', error);
        break;
      }

      if (data && data.length > 0) {
        allRecords = [...allRecords, ...data];
        hasMore = data.length === pageSize;
        page++;
      } else {
        hasMore = false;
      }
    }

    console.log('💰 Cost: Loaded', allRecords.length, 'records');
    return allRecords;
  };

  // Load cost data
  useEffect(() => {
    const loadCosts = async () => {
      if (farmLoading) {
        console.log('💰 Cost: Waiting for farms to load...');
        return;
      }

      setLoading(true);
      setCurrentPage(1);
      try {
        // Fetch ALL data for accurate stats, filtered by active farm IDs
        const data = await fetchAllData(activeFarmIds);

        setAllData(data || []);
        setCosts((data || []).slice(0, itemsPerPage));
        
        // Calculate stats from ALL data
        const uniqueCategories = new Set(data?.map(c => c.fin_subtype_name).filter(Boolean) || []);
        const totalItems = data?.length || 0;
        const totalCost = data?.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0) || 0;
        const avgCostPerItem = totalItems > 0 ? totalCost / totalItems : 0;
        
        setStats({
          totalCategories: uniqueCategories.size,
          totalItems,
          totalCost,
          avgCostPerItem
        });
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCosts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarmFilter, selectedCategory, dateRange, activeFarmIdsKey, farmLoading]);

  // Handle pagination
  const totalPages = Math.ceil(allData.length / itemsPerPage);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
    const startIndex = (page - 1) * itemsPerPage;
    setCosts(allData.slice(startIndex, startIndex + itemsPerPage));
  };

  // Process cost data for chart (use allData for full chart)
  const chartData = useMemo(() => {
    if (!allData.length) return [];
    
    const monthlyData = {};
    allData.forEach(c => {
      if (!c.date_transaction) return;
      const date = new Date(c.date_transaction);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, cost: 0 };
      }
      monthlyData[monthKey].cost += parseFloat(c.amount) || 0;
    });
    
    const sortedData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    
    return sortedData.map(d => ({
      ...d,
      monthLabel: new Date(d.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      value: Math.round(d.cost * 100) / 100
    }));
  }, [allData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'RM0.00';
    return `RM${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost</h1>
        </div>
        <div className="flex items-center space-x-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">Track and manage cost records across all farms</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              label="Farm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cost Categories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCategories}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CubeIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalItems}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <ReceiptPercentIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Cost</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalCost)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Cost/Item</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.avgCostPerItem)}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <CalculatorIcon className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {!loading && chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Trend</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fca5a5" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#fca5a5" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  label={{ value: 'RM', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => [`RM${value.toLocaleString()}`, 'Cost']}
                  labelStyle={{ fontWeight: 600, color: '#374151' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  fill="url(#costGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cost Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-3 text-gray-600">Loading cost data...</span>
          </div>
        ) : costs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {costs.map((cost, idx) => (
                  <tr key={cost.id || idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cost.date_transaction)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {cost.fin_subtype_name || cost.fin_name_type || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cost.name_material || cost.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {cost.inventory_transaction_quantity || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {cost.inventory_transaction_quantity ? formatCurrency(cost.amount / cost.inventory_transaction_quantity) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600">
                      {formatCurrency(cost.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {cost.farm_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <BanknotesIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No cost records found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, allData.length)} of {allData.length} items
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm border rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-green-600 text-white border-green-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cost;
